/**
 * PM pay — which roof_crew_payments rows are the project manager's pay rather
 * than crew job money (Oct 20, 2026, Greg: "although they both go to risol, we
 * pay them differently").
 *
 * Two database triggers write Ramiro's pay onto the Risol rail:
 *   pm_apply_per_build_fee   → a $100 row per completed build, described
 *                              "PM fee — <name>: $100 — <homeowner> (<street>)"
 *   pm_accrue_biweekly_base  → a standalone row (deal_id NULL) every other
 *                              Friday, described "PM biweekly guaranteed — …"
 * A row is PM pay when it has no deal or its description starts with one of
 * those markers. It is matched on the marker, not the name, so a PM retarget
 * keeps working.
 *
 * One test for every surface: the Crew Pay and PM Pay pills on the Commission
 * board and the two paid emails (CrewPayPaidEmailModal). The SQL that bills
 * them to Books (pm_books_bill_make, roof_crew_payment_unapprove) keys off the
 * same two markers.
 *
 * Oct 21, 2026 (pay emails, Ship 2): every other Friday the biweekly base and
 * every per-build fee waiting go to Books as ONE bill of kind 'pm' to Risol,
 * which Shannon pays there as its own payment (its own ACH and email). The rows
 * then carry `books_bill_id` and show the bill chip instead of Mark paid.
 */

import { supabase } from "./supabase";

/** Trigger marker prefixes written by pm_apply_per_build_fee / pm_accrue_biweekly_base. */
export const PM_MARKER_RE = /^\s*PM (fee|biweekly)\b/i;
const PM_BIWEEKLY_RE = /^\s*PM biweekly\b/i;

export interface PmPayRowLike {
  deal_id: string | null;
  additional_work_description: string | null;
}

export function isPmPayRow(r: PmPayRowLike): boolean {
  return r.deal_id === null || PM_MARKER_RE.test(r.additional_work_description ?? "");
}

/** Short label for a PM pay row: the biweekly base or a per-build fee. */
export function pmPayLabel(r: PmPayRowLike): string {
  if (PM_BIWEEKLY_RE.test(r.additional_work_description ?? "")) return "Biweekly base";
  if (r.deal_id !== null) return "Per-build fee";
  return "PM pay";
}

/** Splits rows into crew job money and PM pay, keeping each list's order. */
export function splitPmPay<T extends PmPayRowLike>(rows: T[]): { crew: T[]; pm: T[] } {
  const crew: T[] = [];
  const pm: T[] = [];
  for (const r of rows) (isPmPayRow(r) ? pm : crew).push(r);
  return { crew, pm };
}

/** Resolve the PM's display name from finance_settings → team_roster; falls back to "Ramiro Perez". */
export async function fetchPmName(): Promise<string> {
  try {
    const { data: fs } = await supabase
      .from("finance_settings").select("value_jsonb").eq("key", "pm_team_member_id").maybeSingle();
    const id = typeof fs?.value_jsonb === "string" ? fs.value_jsonb : null;
    if (!id) return "Ramiro Perez";
    const { data: tr } = await supabase.from("team_roster").select("full_name").eq("id", id).maybeSingle();
    return tr?.full_name || "Ramiro Perez";
  } catch {
    return "Ramiro Perez";
  }
}
